import { isLoginError, isUnknownLoginError } from '@customerio/hydra-authentication';
import { NO_OAUTH_RESPONSE, NO_OAUTH_WINDOW, OAUTH_TIMED_OUT, OAuthError } from '@/services/oauth';

/**
 * User-facing label per identity provider, mirroring Ember's
 * `external-identity-provider-label`. Accepts both the `SsoConfiguration`
 * `identity_provider` values (underscore) and the OAuth popup provider ids
 * (hyphen) so either source resolves.
 */
const PROVIDER_LABEL: Record<string, string> = {
  google_oidc: 'Google',
  'google-oidc': 'Google',
  okta_oidc: 'Okta',
  'okta-oidc': 'Okta',
  azure_oidc: 'Azure Active Directory',
  'azure-oidc': 'Azure Active Directory',
  generic_oidc: 'Generic OpenID Connect',
  'generic-oidc': 'Generic OpenID Connect',
  generic_saml: 'SAML 2.0',
};

// Ember defaults to Google when there's no SSO config (the Google One-Click
// context), so an unknown/absent provider falls back to the Google label.
function providerErrorLabel(provider: string | undefined): string {
  return (provider != null ? PROVIDER_LABEL[provider] : undefined) ?? 'Google';
}

function genericProviderMessage(provider: string | undefined): string {
  return `Something went wrong when authenticating with your ${providerErrorLabel(provider)} account, please try again.`;
}

type BackendError = { status?: string; detail?: string };

function backendErrors(err: unknown): BackendError[] {
  const raw = isUnknownLoginError(err)
    ? err.errors
    : typeof err === 'object' && err != null && 'errors' in err
      ? // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
        (err as { errors: unknown }).errors
      : undefined;
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return Array.isArray(raw) ? (raw as BackendError[]) : [];
}

// Fallback message for a failure that matches none of the known login-error
// shapes. Exported semantics live in `isUnrecognizedLoginError`.
const GENERIC_LOGIN_ERROR = 'Problem logging in. Try again later.';

/**
 * Translate a login failure into the user-facing message Ember's `loginTask`
 * catch produced. Typed errors (invalid password, 2FA codes, …) already carry
 * their own message; everything else is normalized here:
 *   - blocked OAuth popup / missing OAuth response → provider-specific copy
 *   - 401 → provider-specific "something went wrong" (SSO/one-click)
 *   - 429 → rate-limit copy
 *   - attempts-limit → contact-support copy
 *   - otherwise → "Problem logging in. Try again later."
 *
 * `ssoProvider` supplies the provider for the 401 case (the API error has no
 * provider of its own); OAuth popup errors carry their own provider.
 */
export function loginErrorMessage(err: unknown, ssoProvider?: string): string {
  if (isLoginError(err)) {
    return err.message;
  }

  if (err instanceof OAuthError) {
    if (err.message === NO_OAUTH_WINDOW) {
      return 'Unable to open authentication window. Please check your pop-up settings.';
    }
    // A timed-out wait is an abandoned popup: same outcome for the reader as no
    // stored response, so it gets the same copy rather than the generic message
    // (which `isUnrecognizedLoginError` would then report to Sentry).
    if (err.message === NO_OAUTH_RESPONSE || err.message === OAUTH_TIMED_OUT) {
      return genericProviderMessage(err.provider);
    }
  }

  const errors = backendErrors(err);
  if (errors.some((e) => e.status === '401')) {
    return genericProviderMessage(ssoProvider);
  }
  if (errors.some((e) => e.status === '429')) {
    return 'Too many attempts. Try again later.';
  }
  if (errors.some((e) => e.detail === 'you have reached your attempts limit')) {
    return 'Access completely denied as you have reached your attempts limit. Please contact win@customer.io for help logging into your account.';
  }

  return GENERIC_LOGIN_ERROR;
}

/**
 * True only when the failure matches none of the known login-error shapes — i.e.
 * `loginErrorMessage` falls through to the generic fallback. These are the
 * genuinely-unexpected errors worth reporting to Sentry; the mapped conditions
 * (invalid password, 2FA codes, blocked OAuth popup, 401, 429, attempts-limit)
 * are expected and shouldn't generate error-level noise.
 */
export function isUnrecognizedLoginError(err: unknown): boolean {
  return loginErrorMessage(err) === GENERIC_LOGIN_ERROR;
}
